import React, { useState, useEffect, useCallback } from "react";
import { GoldenSourceHelper } from "../../../app/Switch_Core_React_Standards";
import {
  fetchSuppliers,
  createSupplierWithAddress,
  revokeSupplierRelationship,
  reactivateSupplierRelationship,
  assignSupplierClassification,
  fetchSupplierClassifications,
  revokeSupplierClassification,
} from "./supplierInteractionService";
import { validateAddress } from "../../common/dogma/dogmaInteractionService";
import { fetchCompanyTaxonomy } from "../companyTaxonomyInteractionService";
import { fetchTaxonomyDetailsByHeader } from "../../site-admin/components/Workflow-esg/services/EsgTaxonomySyncService";
import {
  searchCompanies,
  getCompanyProfile,
  mapRegistryAddress,
  REGISTRY_SUPPORTED_COUNTRIES,
} from "../../common/registry/registryInteractionService";
// 🎯 Alias bypasses the underscore naming rules for react/jsx-pascal-case
import SupplierView from "./SupplierView";

// Collapses repeated whitespace and trims - same convention as
// SEC_RepresentativeController's optional address fields.
const cleanAddressField = (value) => (value || "").replace(/\s+/g, " ").trim();

const EMPTY_FORM = {
  name: "",
  regNumber: "",
  country: "",
  addressLine1: "",
  addressLine2: "",
  town: "",
  county: "",
  postcode: "",
};

export default function SupplierController({ companyId, companyName }) {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [concepts, setConcepts] = useState([]);
  const [conceptsReady, setConceptsReady] = useState(false);
  const [classificationsByRelationship, setClassificationsByRelationship] = useState({});

  // 🎯 Same registry-first flow as SEC_RepresentativeController: check the
  // government company registry (Companies House / DINUM / Zefix) before
  // falling back to manual entry + Dogma address validation.
  const [form, setForm] = useState(EMPTY_FORM);
  // idle | checking | results | confirmed | rejected
  const [registryStatus, setRegistryStatus] = useState("idle");
  const [registryResults, setRegistryResults] = useState([]);
  const [registryError, setRegistryError] = useState("");
  const [selectedMatchId, setSelectedMatchId] = useState(null);
  const [confirmedCompany, setConfirmedCompany] = useState(null);

  const siteKey = process.env.REACT_APP_SITE_KEY;

  const loadSuppliers = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const data = await fetchSuppliers(companyId, { siteKey });
      setSuppliers(data || []);
    } finally {
      setLoading(false);
    }
  }, [companyId, siteKey]);

  // 🎯 Classification options come strictly from the client company's own
  // bound taxonomy (Corp.Z_08_CompanyTaxonomyBinding) - no free-text
  // fallback. If no taxonomy is bound yet, `concepts` stays empty and the
  // view surfaces a pointer to the Taxonomy tab instead.
  const loadConcepts = useCallback(async () => {
    if (!companyId) return;
    const binding = await fetchCompanyTaxonomy(companyId, { siteKey });
    const boundHeaderId = GoldenSourceHelper.get(binding, "HeaderId");
    if (!boundHeaderId) {
      setConcepts([]);
      setConceptsReady(true);
      return;
    }
    const details = await fetchTaxonomyDetailsByHeader(boundHeaderId, { siteKey });
    setConcepts(details || []);
    setConceptsReady(true);
  }, [companyId, siteKey]);

  useEffect(() => {
    loadSuppliers();
    loadConcepts();
  }, [loadSuppliers, loadConcepts]);

  // Editing the primary lookup key invalidates any confirmed/pending registry
  // check - country changes which registry (or none) applies.
  const handlePrimaryFieldChange = useCallback((field, value) => {
    setForm((p) => ({ ...p, [field]: value }));
    setRegistryStatus("idle");
    setRegistryResults([]);
    setRegistryError("");
    setSelectedMatchId(null);
    setConfirmedCompany(null);
  }, []);

  // Name/Reg Number are the registry lookup's own inputs - editing either
  // after a confirmed match invalidates it.
  const handleCompanyFieldChange = useCallback((field, value) => {
    setForm((p) => ({ ...p, [field]: value }));
    setRegistryStatus("idle");
    setRegistryResults([]);
    setRegistryError("");
    setSelectedMatchId(null);
    setConfirmedCompany(null);
  }, []);

  // Same check SEC_RepresentativeController uses: Reg Number first (most
  // precise direct lookup), else a name search. Unsupported countries (DE
  // has no working registry adapter) fall straight to manual entry, silently.
  const handleCheckRegistration = useCallback(async () => {
    const { name, regNumber, country } = form;
    if (!country || !REGISTRY_SUPPORTED_COUNTRIES.includes(country)) {
      setRegistryStatus("rejected");
      return;
    }
    if (!name && !regNumber) {
      setRegistryError("Enter a Supplier Name or Reg Number first.");
      return;
    }

    setRegistryError("");
    setRegistryStatus("checking");
    setSelectedMatchId(null);
    try {
      if (regNumber) {
        const profile = await getCompanyProfile(regNumber, country, { siteKey });
        if (profile) {
          setRegistryResults([profile]);
          setRegistryStatus("results");
          return;
        }
      }
      if (name) {
        const results = await searchCompanies(name, country, { siteKey });
        if (results.length > 0) {
          setRegistryResults(results);
          setRegistryStatus("results");
          return;
        }
      }
      setRegistryStatus("rejected");
    } catch (err) {
      setRegistryError(err.message || "Company registry check failed.");
      setRegistryStatus("idle");
    }
  }, [form, siteKey]);

  const handleSelectMatch = useCallback((companyId) => {
    setSelectedMatchId(companyId);
  }, []);

  const handleConfirmSelection = useCallback(async () => {
    const match = registryResults.find((r) => String(r.companyId) === String(selectedMatchId));
    if (!match) {
      setRegistryError("Select a company from the list first.");
      return;
    }

    setRegistryError("");
    try {
      const profile = match.registeredAddress
        ? match
        : await getCompanyProfile(match.companyId, form.country, { siteKey });

      if (!profile) {
        setRegistryError("Could not retrieve full company details - try again or proceed manually.");
        return;
      }

      const address = mapRegistryAddress(profile);
      const resolvedId = String(profile.companyId ?? match.companyId ?? "");
      setForm((p) => ({
        ...p,
        name: profile.companyName || p.name,
        regNumber: resolvedId || p.regNumber,
        ...address,
      }));
      setConfirmedCompany({ companyName: profile.companyName, companyId: resolvedId, ...address });
      setRegistryStatus("confirmed");
    } catch (err) {
      setRegistryError(err.message || "Could not retrieve full company details.");
    }
  }, [registryResults, selectedMatchId, form.country, siteKey]);

  const handleRejectRegistryMatches = useCallback(() => {
    setRegistryStatus("rejected");
    setRegistryResults([]);
    setRegistryError("");
    setSelectedMatchId(null);
  }, []);

  const handleDiscardConfirmedMatch = useCallback(() => {
    setForm((p) => ({
      ...p,
      addressLine1: "",
      addressLine2: "",
      town: "",
      county: "",
      postcode: "",
    }));
    setConfirmedCompany(null);
    setRegistryResults([]);
    setSelectedMatchId(null);
    setRegistryError("");
    setRegistryStatus("idle");
  }, []);

  /**
   * 🎯 ADD SUPPLIER
   * A supplier is a full CorporateEntity + Address in its own right (matches
   * Corp.Z_01_07_CorporateEntity_Relationship linking two CorporateEntity
   * rows), created in one combined call — same "one button, one outcome"
   * shape as Register Company's registerCompanyFull, minus the Representative
   * grant. Registry-confirmed data skips Dogma re-validation entirely (same
   * 3-case provenance model as Register Company); otherwise address
   * validation (Postcode + Line 1) is attempted quietly first, falling back
   * to whatever was typed manually with no separate error step shown.
   */
  const handleAddSupplier = async () => {
    let working = {
      ...form,
      addressLine2: cleanAddressField(form.addressLine2),
      town: cleanAddressField(form.town),
      county: cleanAddressField(form.county),
    };

    setCreating(true);
    let addressSourceCode = registryStatus === "confirmed" ? "REGISTRY" : "MANUAL_UNVERIFIED";

    if (registryStatus !== "confirmed") {
      try {
        const found = await validateAddress([working.addressLine1, working.postcode], working.country, { siteKey });
        working = {
          ...working,
          addressLine1: found.addressLine1 || working.addressLine1,
          addressLine2: found.addressLine2 || working.addressLine2,
          town: found.town || working.town,
          county: found.county || working.county,
          postcode: found.postcode || working.postcode,
        };
        addressSourceCode = "GOOGLE_VALIDATED";
      } catch (err) {
        // Silent fallback - proceed with whatever was typed manually.
        console.error("Address lookup failed, proceeding with manual entry:", err.message);
      }
    }

    try {
      await createSupplierWithAddress(companyId, { ...working, addressSourceCode }, { siteKey });
      setForm(EMPTY_FORM);
      setConfirmedCompany(null);
      setRegistryResults([]);
      setSelectedMatchId(null);
      setRegistryError("");
      setRegistryStatus("idle");
      loadSuppliers();
    } catch (err) {
      alert(err.message || "Could not create supplier.");
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (supplierEntityId) => {
    try {
      await revokeSupplierRelationship(companyId, supplierEntityId, { siteKey });
      loadSuppliers();
    } catch (err) {
      alert(err.message || "Could not remove supplier.");
    }
  };

  const handleReactivate = async (supplierEntityId) => {
    try {
      await reactivateSupplierRelationship(companyId, supplierEntityId, { siteKey });
      loadSuppliers();
    } catch (err) {
      alert(err.message || "Could not reactivate supplier.");
    }
  };

  const handleLoadClassifications = async (relationshipId) => {
    const data = await fetchSupplierClassifications(relationshipId, { siteKey });
    setClassificationsByRelationship((prev) => ({ ...prev, [relationshipId]: data || [] }));
  };

  const handleToggleClassification = async (relationshipId, taxonomyDetailRowId, isCurrentlyAssigned) => {
    try {
      if (isCurrentlyAssigned) {
        await revokeSupplierClassification(relationshipId, taxonomyDetailRowId, { siteKey });
      } else {
        await assignSupplierClassification(relationshipId, taxonomyDetailRowId, { siteKey });
      }
      handleLoadClassifications(relationshipId);
    } catch (err) {
      alert(err.message || "Could not update classification.");
    }
  };

  return (
    <SupplierView
      companyName={companyName}
      data={suppliers}
      loading={loading}
      creating={creating}
      concepts={concepts}
      conceptsReady={conceptsReady}
      classificationsByRelationship={classificationsByRelationship}
      form={form}
      onPrimaryFieldChange={handlePrimaryFieldChange}
      onCompanyFieldChange={handleCompanyFieldChange}
      registryStatus={registryStatus}
      registryResults={registryResults}
      registryError={registryError}
      selectedMatchId={selectedMatchId}
      confirmedCompany={confirmedCompany}
      onCheckRegistration={handleCheckRegistration}
      onSelectMatch={handleSelectMatch}
      onConfirmSelection={handleConfirmSelection}
      onRejectRegistryMatches={handleRejectRegistryMatches}
      onDiscardConfirmedMatch={handleDiscardConfirmedMatch}
      onAdd={handleAddSupplier}
      onRevoke={handleRevoke}
      onReactivate={handleReactivate}
      onExpandClassification={handleLoadClassifications}
      onToggleClassification={handleToggleClassification}
    />
  );
}
